import { useEffect, useState } from 'react'

import { useIncidentCreationData } from '../hooks/useIncidentCreationData'
import { Controller, UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'

import {
  Box,
  ColorScheme,
  Flex,
  MultilineTextInput,
  Text,
} from '@hivewatch/dark'

import {
  ControlledSelect,
  ControlledTextInput,
  FacilityOptionLabel,
} from '@/components/ui'
import { FacilityType } from '@/graphql/generated/schemas'
import {
  createFacilityAddressFilter,
  getIncidentCategoryOptions,
  getIncidentFacilityOptionsWithAddress,
} from '@/utils/forms/optionBuilders'
import {
  MISCELLANEOUS,
  sortOptions,
  sortOptionsWithLabelLast,
} from '@/utils/forms/optionHelpers'
import {
  SEARCH_DEBOUNCE_TIME,
  getFacilityNoOptionsMessage,
} from '@/utils/forms/validation'

import { AttachFilesButton } from '../components/AttachFilesButton'
import { SelectedFilesPreview } from '../components/SelectedFilesPreview'
import { FormInputsIProps } from '../types/types'
import { MAX_INCIDENT_FILES } from '../utils/constants'
import {
  descriptionRules,
  facilityNameRules,
  incidentCategoryRules,
  nameRules,
} from '../utils/utils'
import { DarkMarketIncidentForm } from './DarkMarketIncidentForm'
import { DarkNormalIncidentForm } from './DarkNormalIncidentForm'

interface DarkIncidentModalBodyIProps {
  form: UseFormMethods<FormInputsIProps>
  selectedAttachments: File[]
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFileRemove: (file: File) => void
  colorScheme: ColorScheme
}

/**
 * Rebrand variant of {@link IncidentModalBody}. Field order, validation rules,
 * the facility-driven reset and the market/normal branch are unchanged; the
 * attachment button and preview have no dark equivalents and are reused.
 */
export const DarkIncidentModalBody = ({
  form,
  selectedAttachments,
  onFileSelect,
  onFileRemove,
  colorScheme,
}: DarkIncidentModalBodyIProps) => {
  const { t } = useTranslation()
  const { watch, getValues, reset, control, errors } = form
  const [facility, setFacility] = useState(null)
  const [facilityName, setFacilityName] = useState('')
  const [debouncedFacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )

  const {
    facilitiesData,
    isFacilitiesLoading,
    isFacilitiesError,
    categoriesData,
  } = useIncidentCreationData({
    facilityName: debouncedFacilityName,
  })
  const facilityOptions = getIncidentFacilityOptionsWithAddress(
    facilitiesData,
    true,
    debouncedFacilityName
  )
  const incidentCategories = sortOptionsWithLabelLast(
    sortOptions(getIncidentCategoryOptions(categoriesData)),
    MISCELLANEOUS
  )

  const watchFacilityOrigin = watch('facilityOrigin')
  const shouldShowMarketFlow = facility?.type === FacilityType.Market

  // Only reset floor/device fields when the operator picks a different facility.
  // Including `facilitiesData` would clear selections after debounced searches.
  useEffect(() => {
    if (watchFacilityOrigin) {
      const selectedFacility = facilitiesData?.facilities?.edges?.find(
        (f) => f?.node?.id === watchFacilityOrigin?.value
      )
      setFacility(selectedFacility?.node)
      reset({
        ...getValues(),
        floorOrigin: null,
        deviceOrigin: null,
      })
    }
  }, [watchFacilityOrigin])

  const labelColor =
    colorScheme === 'light' ? 'light.text.primary' : 'text.primary'

  return (
    <Flex direction='column' gap='16px'>
      <ControlledTextInput
        colorScheme={colorScheme}
        control={control}
        data-testid='createIncidentModal_incidentName'
        errors={errors}
        label={t('incidents:incident_create_name_label')}
        name='name'
        placeholder={t('incidents:incident_create_name_placeholder')}
        rules={nameRules}
        width='100%'
      />

      <ControlledSelect
        colorScheme={colorScheme}
        control={control}
        data-testid='createIncidentModal_facilityName'
        // Filters by facility name, short name, and address. Without
        // filterOption, React Select defaults to label-only search.
        // To modify searchable fields, update createFacilityAddressFilter.
        filterOption={createFacilityAddressFilter}
        formatOptionLabel={(option, meta) => (
          <FacilityOptionLabel
            colorScheme={colorScheme}
            isValueContext={meta?.context === 'value'}
            option={option}
          />
        )}
        isLoading={isFacilitiesLoading}
        label={t('incidents:incident_create_facility_label')}
        name='facilityOrigin'
        noOptionsMessage={({ inputValue }) =>
          getFacilityNoOptionsMessage(
            inputValue,
            isFacilitiesError,
            t('common:facility_search_error')
          )
        }
        onInputChange={setFacilityName}
        options={facilityOptions}
        placeholder={t('incidents:incident_create_facility_placeholder')}
        rules={facilityNameRules}
      />

      <ControlledSelect
        colorScheme={colorScheme}
        control={control}
        data-testid='createIncidentModal_incidentCategory'
        label={t('incidents:incident_create_category_label')}
        name='incidentCategory'
        options={incidentCategories}
        placeholder={t('incidents:incident_create_category_placeholder')}
        rules={incidentCategoryRules}
      />

      {shouldShowMarketFlow ? (
        <DarkMarketIncidentForm colorScheme={colorScheme} form={form} />
      ) : (
        <DarkNormalIncidentForm
          colorScheme={colorScheme}
          facility={facility}
          form={form}
        />
      )}

      {/* The label is rendered here rather than through MultilineTextInput's
          own `label` prop: that prop wraps the control in a section frame
          (side borders, 20px padding, 8px mono caps) built for the incident
          drawer, which reads nothing like the other fields in this form. */}
      <Controller
        control={control}
        defaultValue=''
        name='description'
        render={({ value, onChange }) => (
          <Box>
            <Text color={labelColor} mb='4px' textStyle='label'>
              {t('incidents:incident_create_description_label')}
            </Text>
            <MultilineTextInput
              colorScheme={colorScheme}
              data-testid='createIncidentModal_description'
              hasWarning={Boolean(errors.description)}
              onChange={(e) => onChange(e.target.value)}
              placeholder={t(
                'incidents:incident_create_description_placeholder'
              )}
              value={value ?? ''}
              warningText={errors.description?.message}
              width='100%'
            />
          </Box>
        )}
        rules={descriptionRules}
      />

      <Box>
        <Text color={labelColor} mb='4px' textStyle='label'>
          {t('incidents:incident_create_attachments_label')}
        </Text>
        <AttachFilesButton
          colorScheme={colorScheme}
          handleFileSelect={onFileSelect}
          maxFiles={MAX_INCIDENT_FILES}
          selectedFiles={selectedAttachments}
        />
        {selectedAttachments.length > 0 && (
          <SelectedFilesPreview
            removeFile={onFileRemove}
            selectedFiles={selectedAttachments}
          />
        )}
      </Box>
    </Flex>
  )
}
